/**
 * Web push (26 Oct 2026): this browser signs up with Firebase Cloud Messaging and tells the server its token, so
 * Task Force can notify the person even when it is closed. The service worker (public/push-sw.js) shows what comes.
 *
 * - Turned on per browser, by the person, from Notification settings (the browser asks for permission then).
 * - The token is checked again on each visit (Firebase renews them), quietly, after the page is up.
 * - It belongs to the person who turned it on: signing out forgets it here, and the server forgets it with the
 *   sign-in, so the next person on a shared computer starts with push off.
 * - Firebase's code loads only when it is needed, never with the page.
 */
import { useQuery } from '@tanstack/react-query';
import { api, type Me } from '../lib/api';
import { currentSurface, track } from '../lib/analytics';

/** This site's Firebase project (public by design: it names the project, it is not a key to anything). */
const FIREBASE = {
  apiKey: 'AIzaSyA_wISUawGrvKFJ078Jg5Evbl75o158TyE',
  authDomain: 'dubizzlelabs-taskforce-prod.firebaseapp.com',
  projectId: 'dubizzlelabs-taskforce-prod',
  storageBucket: 'dubizzlelabs-taskforce-prod.firebasestorage.app',
  messagingSenderId: '263396338579',
  appId: '1:263396338579:web:6ece5b1d08bf8ed3cfd666',
};
const SW = '/push-sw.js';
/** This browser's sign-up: the token and whose it is. */
const KEY = 'tf-push';

export interface TfPushState {
  ready: boolean;
  vapidKey: string | null;
  browsers: Array<{ id: string; label: string; thisSignIn: boolean; createdAt: string; lastSeenAt: string }>;
  /** Their phones with the app, and whether each takes pushes (26 Oct 2026). */
  phones?: Array<{ id: string; name: string; platform: string; appVersion: string; push: boolean }>;
  /** Why their last pushes failed, newest first. */
  problems?: Array<{ error: string; at: string }>;
}
/**
 * The test push and why pushes failed are for admins (27 Oct 2026): Task Force admins and super admins, as /api/me
 * says (access.admin.taskforce counts Task Force owners and admins, and super admins). The server refuses the test
 * push to anyone else as well.
 */
export const canTestPush = (me: Me | null | undefined): boolean =>
  !!me?.access && (me.access.admin.taskforce || me.access.admin.super);
export const usePushState = () =>
  useQuery({ queryKey: ['tf', 'push'], queryFn: () => api<TfPushState>('/api/tf/push'), staleTime: 60_000 });

type Saved = { token: string; userId: string };
const saved = (): Saved | null => {
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? 'null') as Saved | null;
  } catch {
    return null;
  }
};
const save = (s: Saved | null) => {
  try {
    if (s) localStorage.setItem(KEY, JSON.stringify(s));
    else localStorage.removeItem(KEY);
  } catch {
    // Private browsing: it lasts this visit only.
  }
};

/** Whether this browser can take push, and if not, why (in words for the settings). */
export function pushSupport(): { ok: true } | { ok: false; why: string } {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator) || !('PushManager' in window))
    return /iPhone|iPad/.test(navigator.userAgent) && !(navigator as { standalone?: boolean }).standalone
      ? {
          ok: false,
          why: 'On an iPhone or iPad, add Task Force to your Home Screen (Share → Add to Home Screen), open it from there, then turn this on.',
        }
      : { ok: false, why: 'This browser cannot get push notifications.' };
  if (!('Notification' in window)) return { ok: false, why: 'This browser cannot show notifications.' };
  return { ok: true };
}

/** Whether push is on in this browser for this person. */
export const pushOnHere = (userId: string | undefined) =>
  !!userId &&
  saved()?.userId === userId &&
  typeof Notification !== 'undefined' &&
  Notification.permission === 'granted';

/** "Chrome on macOS", for the list of browsers in the settings. */
export function browserLabel(ua = navigator.userAgent): string {
  const b = /Edg\//.test(ua)
    ? 'Edge'
    : /OPR\//.test(ua)
      ? 'Opera'
      : /Firefox\//.test(ua)
        ? 'Firefox'
        : /Chrome\//.test(ua)
          ? 'Chrome'
          : /Safari\//.test(ua)
            ? 'Safari'
            : 'A browser';
  const os = /Windows/.test(ua)
    ? 'Windows'
    : /iPhone|iPad/.test(ua)
      ? 'iOS'
      : /Mac OS X/.test(ua)
        ? 'macOS'
        : /Android/.test(ua)
          ? 'Android'
          : /Linux/.test(ua)
            ? 'Linux'
            : '';
  return os ? `${b} on ${os}` : b;
}

/** Firebase's messaging, loaded on first use. */
async function messaging() {
  const [{ getApp, getApps, initializeApp }, m] = await Promise.all([
    import('firebase/app'),
    import('firebase/messaging'),
  ]);
  const app = getApps().length ? getApp() : initializeApp(FIREBASE);
  return { m, msg: m.getMessaging(app) };
}

/** This browser's Firebase token (signing it up with the browser's push service if it is not yet). */
async function token(vapidKey: string): Promise<string> {
  const reg = await navigator.serviceWorker.register(SW, { scope: '/' });
  await navigator.serviceWorker.ready;
  const { m, msg } = await messaging();
  return m.getToken(msg, { vapidKey, serviceWorkerRegistration: reg });
}

/**
 * Turns push on in this browser. Call it straight from a click: the browser only asks for permission then.
 * Throws with a message for the person when it cannot.
 */
export async function enablePush(vapidKey: string, userId: string): Promise<void> {
  const s = pushSupport();
  if (!s.ok) throw new Error(s.why);
  let got = Notification.permission;
  if (got !== 'granted') {
    got = await Notification.requestPermission();
    track('push_permission', {
      surface: currentSurface(),
      result: got === 'granted' ? 'granted' : got === 'denied' ? 'denied' : 'dismissed',
    });
  }
  if (got !== 'granted')
    throw new Error(
      got === 'denied'
        ? 'Notifications are blocked for this site. Allow them in your browser’s site settings (the icon left of the address), then try again.'
        : 'Notifications were not allowed.',
    );
  const t = await token(vapidKey);
  await api('/api/tf/push/browsers', { method: 'POST', json: { token: t, label: browserLabel() } });
  save({ token: t, userId });
}

/** Turns push off in this browser: Firebase forgets the token, and so does the server. */
export async function disablePush(): Promise<void> {
  const s = saved();
  save(null);
  if (!s) return;
  await api('/api/tf/push/browsers/remove', { method: 'POST', json: { token: s.token } }).catch(() => undefined);
  try {
    const { m, msg } = await messaging();
    await m.deleteToken(msg);
  } catch {
    // Already gone.
  }
}

/** Forgets this browser's sign-up when its person signs out (the server forgets it with the sign-in). */
export const forgetPush = () => save(null);

/**
 * On each visit, once the page is up: if push is on here for this person, check the token (Firebase renews them)
 * and tell the server, which also ties it to this sign-in. Permission taken away since: push goes off here.
 */
export async function refreshPush(vapidKey: string, userId: string): Promise<void> {
  const s = saved();
  if (!s || s.userId !== userId) return;
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
    await disablePush();
    return;
  }
  const t = await token(vapidKey);
  await api('/api/tf/push/browsers', { method: 'POST', json: { token: t, label: browserLabel() } });
  if (t !== s.token) save({ token: t, userId });
}
